import type {
  DatesSetArg,
  EventClickArg,
  EventContentArg,
  EventInput,
} from "@fullcalendar/core/index.js";
import pluginDayGrid from "@fullcalendar/daygrid";
import pluginInteration from "@fullcalendar/interaction";
import pluginList from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import pluginTimeGrid from "@fullcalendar/timegrid";
import { useGetScheduledEventsQuery } from "@package/ui-gql/index-data-access";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  ITEM_GRID_PATH_NEW,
  SidebarEntryPath,
} from "../../../../constants/contants";
import {
  setSchedulerArgsCreate,
  setSchedulerSelectedEventId,
} from "../../../../store/slices/slice-scheduler";
import { useAppDispatch, useAppSelector } from "../../../../store/store";
import { convertScheduledEventsToCalendarInputs } from "./calendar-utils";
import { SchedulerEventChip } from "./SchedulerEventChip";
import {
  SchedulerEventPopover,
  type SchedulerPopoverOffset,
} from "./SchedulerEventPopover";
import { SchedulerHeader } from "./SchedulerHeader";

export function SchedulerCalendar() {
  const dispatch = useAppDispatch();
  const selectedEventId = useAppSelector(
    (state) => state.scheduler.selectedEventId,
  );
  const [dateSetArg, setDateSetArg] = useState<DatesSetArg>();
  // The popover's screen position is transient view state, not something
  // that should survive navigation -- it lives here rather than in the
  // scheduler redux slice alongside selectedEventId/argsCreate.
  const [popoverOffset, setPopoverOffset] =
    useState<SchedulerPopoverOffset | null>(null);
  const calendarRef = useRef<FullCalendar>(null);

  const { search } = useLocation();
  const navigate = useNavigate();

  const { data: eventsData } = useGetScheduledEventsQuery({
    variables: {
      filter: {
        start_gte: dateSetArg?.startStr,
        start_lte: dateSetArg?.endStr,
      },
      page: 0,
      perPage: 1000,
    },
  });

  const events = useMemo<EventInput[]>(
    () =>
      convertScheduledEventsToCalendarInputs(
        eventsData?.items,
        selectedEventId,
      ),
    [eventsData?.items, selectedEventId],
  );

  const closePopover = useCallback(() => {
    dispatch(setSchedulerSelectedEventId());
    setPopoverOffset(null);
  }, [dispatch]);

  const changeView = useCallback((view: string) => {
    calendarRef.current?.getApi().changeView(view);
  }, []);

  // The popover's target position is captured once, at click time, and
  // never re-measured -- so it goes stale (detaches from its event) if the
  // calendar's own scroll container scrolls (e.g. a long timeGridDay/Week
  // view) or the window resizes. Closing rather than trying to track the
  // event live is the simplest correct behavior here.
  useEffect(
    function effectOnScrollOrResizeClosePopover() {
      if (!popoverOffset) {
        return;
      }

      // capture: true so this also catches scroll events on FullCalendar's
      // internal scroll containers, which don't bubble to window.
      window.addEventListener("scroll", closePopover, true);
      window.addEventListener("resize", closePopover);

      return () => {
        window.removeEventListener("scroll", closePopover, true);
        window.removeEventListener("resize", closePopover);
      };
    },
    [closePopover, popoverOffset],
  );

  return (
    <>
      <SchedulerHeader
        onChangeView={changeView}
        onNext={() => calendarRef.current?.getApi().next()}
        onPrev={() => calendarRef.current?.getApi().prev()}
        onToday={() => calendarRef.current?.getApi().today()}
        title={dateSetArg?.view.title ?? ""}
        view={dateSetArg?.view.type ?? "dayGridMonth"}
      />
      <div className="scheduler-calendar mt-4">
        <FullCalendar
          datesSet={(arg) => setDateSetArg(arg)}
          editable
          eventContent={(arg: EventContentArg) => (
            <SchedulerEventChip {...arg} />
          )}
          events={events}
          eventClick={(arg: EventClickArg) => {
            const rect = arg.el.getBoundingClientRect();

            dispatch(setSchedulerSelectedEventId(arg.event.id));
            // The popover's target is `position: fixed`, so these
            // viewport-relative coordinates from getBoundingClientRect() are
            // used as-is, with no scroll offset to add.
            setPopoverOffset({
              left: rect.right + 8,
              top: rect.top,
            });
          }}
          headerToolbar={false}
          height="auto"
          initialView="dayGridMonth"
          plugins={[
            pluginDayGrid,
            pluginInteration,
            pluginList,
            pluginTimeGrid,
          ]}
          ref={calendarRef}
          select={(arg) => {
            dispatch(setSchedulerSelectedEventId());
            dispatch(
              setSchedulerArgsCreate({
                start: arg.startStr,
                end: arg.endStr,
              }),
            );
            navigate(
              `/${SidebarEntryPath.scheduler}/${ITEM_GRID_PATH_NEW}${search}`,
            );
          }}
          selectable
          selectMirror
        />
      </div>
      <SchedulerEventPopover
        eventId={selectedEventId ?? null}
        offset={popoverOffset}
        onClose={closePopover}
      />
    </>
  );
}
